import axios from "axios";
import { API_URL } from "constants/ApiUrl";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { FaEye, FaPlus, FaTrashAlt } from "react-icons/fa";
import ReactPlayer from "react-player";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { toast } from "react-toastify";
import {
  Alert,
  Button,
  Col,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Row,
  Spinner,
} from "reactstrap";
import { getPlayerById } from "store/slices/agentPlayerSlice";

const AgentPlayerVideos = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [file, setFile] = useState(null);
  const [uploadError, setUploadError] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const { getProfileLoading, player, error } = useSelector(
    (state) => state.AgentPlayer
  );

  //   useEffect(() => {
  //     if (id) {
  //       dispatch(getPlayerById(id));
  //     }
  //   }, [dispatch, id]);

  if (getProfileLoading) {
    return (
      <div style={{ textAlign: "center", padding: "20px" }}>
        <Spinner color="primary" />
        <div>Chargement en cours...</div>
      </div>
    );
  }

  if (error) return <Alert color="danger">{error.message}</Alert>;

  const toggleModal = () => {
    setModalOpen(!modalOpen);
    setUploadError(null);
    setFile(null);
  };

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!file) {
      setUploadError("Please provide both a title and video file.");
      return;
    }
    setUploading(true);

    const formData = new FormData();
    formData.append("video", file);

    try {
      const res = await fetch(`${API_URL}/api/player/addVideo`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${localStorage.getItem("authUser")}`,
        },
        body: formData,
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Upload failed");
      }
      //   await getVideos();
      toggleModal();
      toast.success("Video added successfully");
    } catch (err) {
      setUploadError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (videoId) => {
    if (!window.confirm("Are you sure you want to delete this video?")) return;
    try {
      const res = await fetch(`${API_URL}/api/player/videos/${videoId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("authUser")}`,
        },
      });
      if (!res.ok) {
        const errData = await res.json();
        throw new Error(errData.message || "Delete failed");
      }
      //   await getVideos();
      toast.success("Video deleted successfully");
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <Row>
        {player?.videos === null ? (
          <p>{t("playerInfo.video.loadingVideo")}</p>
        ) : player?.videos.length === 0 ? (
          <p>{t("playerInfo.video.noVideo")}</p>
        ) : (
          player?.videos.map((video) => (
            <Col md="4" key={video._id}>
              <VideoItem video={video} onDelete={handleDelete} />
            </Col>
          ))
        )}
      </Row>

      <div className="mb-4">
        <Button
          onClick={toggleModal}
          style={{
            backgroundColor: "#4896A5",
            border: "none",
            display: "inline-flex",
            padding: 0,
          }}
        >
          <span
            style={{
              color: "#fff",
              padding: "0.5rem 1rem",
              fontFamily: "Poppins, sans-serif",
              fontSize: "1rem",
            }}
          >
            {t("playerInfo.video.addVideo")}
          </span>

          <span
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderLeft: "1px solid rgba(255,255,255,0.5)",
              padding: "0.5rem 0.75rem",
            }}
          >
            <div
              style={{
                border: "1px solid #fff",
                borderRadius: "50%",
                padding: "0.25rem",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <FaPlus style={{ color: "#fff" }} />
            </div>
          </span>
        </Button>
        <Modal isOpen={modalOpen} toggle={toggleModal}>
          <ModalHeader toggle={toggleModal}>
            {t("playerInfo.video.addVideo")}
          </ModalHeader>
          <ModalBody>
            {uploadError && <Alert color="danger">{uploadError}</Alert>}
            <Form onSubmit={handleUpload}>
              <FormGroup>
                <Label for="videoFile">{t("playerInfo.video.videoFile")}</Label>
                <Input
                  id="videoFile"
                  type="file"
                  accept="video/*"
                  onChange={handleFileChange}
                />
              </FormGroup>

              <Button color="success" type="submit" disabled={uploading}>
                {uploading
                  ? t("playerInfo.video.downloading")
                  : t("playerInfo.video.send")}
              </Button>
            </Form>
          </ModalBody>
        </Modal>
      </div>
    </>
  );
};

const VideoItem = ({ video, onDelete }) => {
  const { url, title, views, createdAt, _id } = video;
  const formattedDate = new Date(createdAt).toLocaleDateString();

  return (
    <div style={styles.container}>
      <ReactPlayer
        url={`${API_URL}/${url}`}
        controls
        width="100%"
        height="200px"
      />
      <div style={styles.info}>
        <Row className="mb-2">
          <Col md={10}>
            <div style={styles.title}>{title}</div>
          </Col>
          <Col md={2} className="text-end">
            <FaTrashAlt
              style={styles.trashIcon}
              onClick={() => onDelete(_id)}
            />
          </Col>
        </Row>
        <Row>
          <Col>
            <div style={styles.dateAndViews}>
              <span>{formattedDate}</span>
              <span style={{ opacity: 0.5 }}>
                ({views}) <FaEye style={styles.icon} />
              </span>
            </div>
          </Col>
        </Row>
      </div>
    </div>
  );
};

const styles = {
  container: {
    width: "100%",
    maxWidth: "800px",
    margin: "20px auto",
    border: "1px solid #ddd",
    borderRadius: "8px",
    overflow: "hidden",
    boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
  },
  info: {
    padding: "10px 15px",
    backgroundColor: "#F1F1F1",
    borderTop: "1px solid #ddd",
  },
  title: {
    fontSize: "16px",
    font: "Poppins",
    color: "#000000",
  },
  trashIcon: {
    color: "red",
    cursor: "pointer",
  },
  views: {
    display: "flex",
    alignItems: "center",
    fontSize: "14px",
    color: "#555",
  },
  icon: {
    marginLeft: "5px",
    color: "#555",
  },
  dateAndViews: {
    display: "flex",
    justifyContent: "space-between",
    fontSize: "14px",
    font: "Poppins",
    color: "#000000",
  },
};

export default AgentPlayerVideos;
